import { useAppSelector } from "@/store/store-hooks";
import { selectAllIElementsOfType } from "@faro-lotv/app-component-toolbox";
import {
  DatePicker,
  Dropdown,
  FaroDialog,
  TextField,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { assert, GUID, removeExtension } from "@faro-lotv/foundation";
import {
  IElementSection,
  IElementType,
  IElementTypeHint,
  IPose,
  isIElementSectionWithTypeHint,
  isIElementTimeseriesDataSession,
} from "@faro-lotv/ielement-types";
import { selectIElement } from "@faro-lotv/project-source";
import { createMutationAddGaussianSplats } from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useMemo, useState, type JSX } from "react";
import { updateProject } from "../project-provider/update-project";
import { useUploadElement } from "./use-upload-element";
import {
  CommonUpdateProjectParams,
  UploadElementType,
} from "./use-upload-element-types";

export type UpdateProjectWithGaussianSplatsParams = {
  type: UploadElementType.gaussianSplats;
};

/**
 * Function that sends the AddGaussianSplats mutation to the project api and update the local copy of the project
 */
export async function updateProjectWithGaussianSplats({
  appStore,
  dispatch,
  projectApi,
  file,
  name,
  createdAt,
  areaId,
  downloadUrl,
  md5Hash,
}: CommonUpdateProjectParams &
  UpdateProjectWithGaussianSplatsParams): Promise<void> {
  assert(
    areaId,
    "AreaId for updating project with GaussianSplats is not specified.",
  );
  const area = selectIElement(areaId)(appStore.getState());
  assert(area, `No area found with ID ${areaId}`);

  // Fetch the time series from the backend, so we can properly
  // add the cloud to an area not yet loaded by the viewer
  const { page: timeSeries } = await projectApi.getIElements({
    types: [IElementType.timeSeries],
    typeHints: [IElementTypeHint.dataSession],
    ancestorIds: [area.id],
  });
  assert(
    timeSeries.length === 0 || isIElementTimeseriesDataSession(timeSeries[0]),
    "Fetching timeseries returned unexpected result",
  );

  // by default, the gaussian splats dataset is rotated upside down,
  // therefore the pose has a 180° rotation around the X axis
  const pose: IPose = {
    isWorldPose: false,
    isWorldRot: false,
    rot: { x: 1, y: 0, z: 0, w: 0 },
    pos: null,
    scale: null,
    gps: null,
  };

  // Add new GaussianSplat to the project
  const addGaussianSplatMutation = createMutationAddGaussianSplats({
    rootId: area.rootId,
    areaId: area.id,
    timeseriesId: timeSeries[0]?.id,
    name,
    createdAt: createdAt.toISOString(),
    uri: downloadUrl,
    fileName: file.name,
    fileSize: file.size,
    md5Hash,
    pose,
  });

  await projectApi.applyMutations([addGaussianSplatMutation]);

  // Fetch the changed sub-tree and update the local copy of the project
  await dispatch(
    updateProject({
      projectApi,
      iElementQuery: {
        // We need to refresh from the floor as it may have changed if a new timeseries have been updated
        ancestorIds: [area.id],
      },
    }),
  );
}

type GaussianSplatsDialogProps = {
  /** The area to upload the Gaussian Splats to */
  area?: IElementSection;
  /** The file to upload */
  file: File;
  /** Method to set the Gaussian Splats file */
  setGaussianSplatsFile(file: File | undefined): void;
  /** Callback to call when the file is uploaded */
  onFileUploaded?(): void;
};

/**
 * @returns a dialog for uploading Gaussian Splats files.
 * Allows the user to set the name, date, and associated area for the Gaussian Splats.
 */
export function GaussianSplatsDialog({
  area,
  file,
  setGaussianSplatsFile,
  onFileUploaded,
}: GaussianSplatsDialogProps): JSX.Element {
  const uploadElement = useUploadElement();

  // Initial values for the dialog
  const initialName = useMemo(() => removeExtension(file.name), [file.name]);
  const initialDate = useMemo(
    () => new Date(file.lastModified),
    [file.lastModified],
  );

  const [gaussianSplatsName, setGaussianSplatsName] = useState<string>(
    removeExtension(file.name),
  );
  const [gaussianSplatsDate, setGaussianSplatsDate] = useState<
    Date | undefined
  >(new Date(file.lastModified));

  const [areaId, setAreaId] = useState<GUID | undefined>(area?.id);
  const allAreaSections = useAppSelector(
    selectAllIElementsOfType((element): element is IElementSection =>
      isIElementSectionWithTypeHint(element, IElementTypeHint.area),
    ),
    isEqual,
  );

  return (
    <FaroDialog
      title="Import Gaussian Splats"
      open
      onConfirm={() => {
        uploadElement({
          type: UploadElementType.gaussianSplats,
          file,
          areaId,
          name: gaussianSplatsName,
          createdAt: gaussianSplatsDate ?? initialDate,
          onFileUploaded,
        });
        setGaussianSplatsFile(undefined);
      }}
      onCancel={() => setGaussianSplatsFile(undefined)}
    >
      <Stack spacing={2} sx={{ maxWidth: "320px" }}>
        <TextField
          label="Gaussian Splats name"
          text={gaussianSplatsName}
          onTextChanged={setGaussianSplatsName}
          error={gaussianSplatsName ? undefined : "This field is required"}
          helpText={
            initialName ? (
              <>
                Original Name:{" "}
                <TranslateVar name="name">{initialName}</TranslateVar>
              </>
            ) : undefined
          }
        />

        <DatePicker
          label="Time point"
          initialDate={initialDate}
          date={gaussianSplatsDate}
          onChange={setGaussianSplatsDate}
        />

        <Dropdown
          label="Associated area"
          value={areaId}
          options={allAreaSections.map((area) => ({
            key: area.id,
            value: area.id,
            label: area.name,
          }))}
          onChange={(ev) => {
            setAreaId(ev.target.value);
          }}
          shouldCapitalize={false}
        />
      </Stack>
    </FaroDialog>
  );
}
